import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph, CheckGlyph, Stage } from '@/components/agents/primitives';
import { SourceIcon, type SourceKind } from '@/components/audio/audioUi';
import Spinner from '@/components/Spinner';

import { AUDIOS_IN_PLAN } from '@/constants/common';

import { useLandingT, useStartHref } from './kit';

/** Last call to action, with a few finished files beside it. */
export default function FinalCta({
  cta = 'upload',
}: {
  cta?: 'upload' | 'record';
}) {
  const { t } = useLandingT();
  const startHref = useStartHref(cta);

  const files: { name: string; kind: SourceKind; done: boolean }[] = [
    { name: 'team-standup.m4a', kind: 'file', done: true },
    { name: 'episode-42-final.mp3', kind: 'file', done: true },
    { name: 'lecture-7-cell-biology.mp4', kind: 'file', done: false },
  ];

  return (
    <section className='mx-auto max-w-7xl px-3 pb-24 pt-8 sm:px-6 sm:pb-32 lg:px-8'>
      <Stage
        variant='indigo'
        halftoneAt='100% 100%'
        className='px-6 py-16 sm:px-14 sm:py-24'
      >
        <div className='relative grid items-center gap-14 lg:grid-cols-[1.6fr_1fr]'>
          <div>
            <h2
              className={clsxm(
                styles.display,
                'text-[2.25rem] font-semibold leading-[1.06] text-white sm:text-5xl lg:text-[3.5rem]',
              )}
            >
              {t('Your next recording, as text in minutes.')}
            </h2>
            <p className='mt-6 max-w-lg text-lg leading-8 text-primary-100'>
              {t(
                '{{count}} free transcriptions every month. No credit card needed.',
                { count: AUDIOS_IN_PLAN.Free },
              )}
            </p>
            <div className='mt-10 flex flex-wrap items-center gap-3'>
              <Link
                href={startHref}
                className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-primary-700 shadow-[0_10px_30px_-10px_rgba(20,10,80,0.6)] transition-colors duration-200 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {cta === 'record'
                  ? t('Start recording')
                  : t('Start transcribing')}
                <ArrowGlyph />
              </Link>
              <Link
                href='/pricing'
                className='inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/35 transition duration-200 hover:bg-white/10 hover:ring-white/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('See plans')}
              </Link>
            </div>
          </div>

          <div aria-hidden='true' className='hidden lg:block'>
            <div className={clsxm(styles.float, 'ms-auto max-w-sm space-y-3')}>
              {files.map((file, index) => (
                <div
                  key={file.name}
                  className={clsxm(
                    'flex items-center gap-3 rounded-2xl px-4 py-3 shadow-[0_20px_40px_-20px_rgba(20,10,80,0.6)]',
                    index === 0
                      ? 'bg-white text-[#10101c]'
                      : 'bg-white/15 text-white ring-1 ring-inset ring-white/20',
                    index === 1 && 'ms-6',
                    index === 2 && 'ms-12',
                  )}
                >
                  <SourceIcon
                    kind={file.kind}
                    className={clsxm(
                      'h-9 w-9 rounded-xl',
                      index === 0
                        ? ''
                        : 'bg-white/10 ring-white/20 [&_svg]:text-white',
                    )}
                  />
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate font-mono text-[13px] font-semibold'>
                      {file.name}
                    </span>
                    <span
                      className={clsxm(
                        'mt-0.5 flex items-center gap-1.5 text-xs',
                        index === 0 ? 'text-emerald-600' : 'text-primary-100',
                      )}
                    >
                      {file.done ? (
                        <CheckGlyph className='h-3.5 w-3.5' />
                      ) : (
                        <Spinner className='h-3 w-3 text-primary-100' />
                      )}
                      {file.done ? t('Transcribed') : t('Transcribing…')}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Stage>
    </section>
  );
}
